import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AgentVisibility } from "@/api/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AgentDraft } from "@/features/agents/components/AgentDraft";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { useUsers } from "@/features/users/hooks/useUsers";

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
}

const MODES: AgentVisibility[] = ["company", "team_restricted", "private"];

export function VisibilityTab({ draft, onChange }: Props) {
  const { t } = useTranslation("agents");
  const teamsQuery = useTeams();
  const usersQuery = useUsers({ is_active: true });
  const [teamSearch, setTeamSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");

  function setMode(mode: AgentVisibility) {
    onChange({ ...draft, visibility: mode });
  }

  function toggleTeam(teamId: string, on: boolean) {
    const set = new Set(draft.visibility_team_ids);
    if (on) set.add(teamId);
    else set.delete(teamId);
    onChange({ ...draft, visibility_team_ids: [...set] });
  }

  function toggleUser(userId: string, on: boolean) {
    const set = new Set(draft.visibility_user_ids);
    if (on) set.add(userId);
    else set.delete(userId);
    onChange({ ...draft, visibility_user_ids: [...set] });
  }

  const filteredTeams = useMemo(() => {
    if (!teamsQuery.data) return [];
    const q = teamSearch.trim().toLowerCase();
    return teamsQuery.data.filter((t) => t.name.toLowerCase().includes(q));
  }, [teamsQuery.data, teamSearch]);

  const filteredUsers = useMemo(() => {
    if (!usersQuery.data) return [];
    const q = userSearch.trim().toLowerCase();
    return usersQuery.data.filter(
      (u) =>
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [usersQuery.data, userSearch]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("visibilityTab.mode")}</Label>
        <div role="radiogroup" className="grid gap-2 sm:grid-cols-3">
          {MODES.map((mode) => (
            <label
              key={mode}
              className={`flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm ${
                draft.visibility === mode
                  ? "border-primary bg-accent"
                  : "border-input"
              }`}
            >
              <input
                type="radio"
                name="visibility-mode"
                value={mode}
                checked={draft.visibility === mode}
                onChange={() => setMode(mode)}
              />
              {t(`visibilityTab.modes.${mode}`)}
            </label>
          ))}
        </div>
      </div>

      {draft.visibility === "team_restricted" ? (
        <div className="space-y-2">
          <Label>{t("visibilityTab.teams")}</Label>
          <Input
            placeholder={t("visibilityTab.searchTeam")}
            value={teamSearch}
            onChange={(e) => setTeamSearch(e.target.value)}
          />
          <ul className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
            {filteredTeams.map((team) => {
              const checked = draft.visibility_team_ids.includes(team.id);
              return (
                <li key={team.id}>
                  <label className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleTeam(team.id, e.target.checked)}
                    />
                    <span className="text-sm">{team.name}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {draft.visibility === "private" ? (
        <div className="space-y-2">
          <Label>{t("visibilityTab.users")}</Label>
          <Input
            placeholder={t("visibilityTab.searchUser")}
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />
          <ul className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
            {filteredUsers.map((user) => {
              const checked = draft.visibility_user_ids.includes(user.id);
              return (
                <li key={user.id}>
                  <label className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleUser(user.id, e.target.checked)}
                    />
                    <span className="text-sm">
                      {user.name}{" "}
                      <span className="text-xs text-muted-foreground">
                        {user.email}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
